import { ProductComparisonStorage } from './product-comparison-storage';
import { QBus } from '@otto-ec/global-resources/event-q-bus';

export type ProductComparisonApi = {
    /**
     * Adds a variation ID to the list for a context ID, creating the list if necessary
     *
     * @param contextId
     * @param variationId
     * @returns the variation ID if it was added, or `null` if the variation ID was already in the list
     */
    addToList: (contextId: string, variationId: string) => Promise<string | null>;

    /**
     * Removes a variation ID from the list for a context ID, removing the list if the variation ID was the sole remaining element
     *
     * @param contextId
     * @param variationId
     * @returns the variation ID if it was removed, or `null` if the variation ID was not in the list or there was no list for the context ID
     */
    removeFromList: (contextId: string, variationId: string) => Promise<string | null>;

    /**
     * Removes the list for a context ID
     *
     * @param contextId
     * @returns the context ID if the list was removed, or `null` if there was no list for the context ID
     */
    removeList: (contextId: string) => Promise<string | null>;

    /**
     * Returns the variation IDs associated with a context ID
     *
     * @param contextId
     * @returns the variation IDs associated with the context ID, or `null` if no variation IDs are associated with the context ID
     */
    getList: (contextId: string) => Promise<string[] | null>;

    /**
     * Returns the context IDs of existing lists
     *
     * @returns the context IDs of existing lists, or `null` if no variation IDs are associated with any context IDs yet
     */
    getLists: () => Promise<string[] | null>;

    /**
     * Returns whether a variation ID is associated with a context ID
     *
     * @param contextId
     * @param variationId
     * @returns whether a variation ID is associated with a context ID
     */
    isOnList: (contextId: string, variationId: string) => Promise<boolean>;

    /**
     * Returns the link for the product comparison page for a context ID
     *
     * The link will refer to the product comparison page for the variations selected by the user for the given product
     * base class (the context ID).
     *
     * @param contextId
     * @returns the link for the product comparison page for a context ID, or `null` if no list is associated with the context ID
     */
    getLinkForList: (contextId: string) => Promise<string | null>;
};

export function isProductComparisonApi(object: unknown): object is ProductComparisonApi {
    return (
        object !== null &&
        typeof object === 'object' &&
        ['addToList', 'removeFromList', 'removeList', 'getList', 'getLists', 'isOnList', 'getLinkForList'].every(key => key in object)
    );
}

export const createProductComparison = (storage: ProductComparisonStorage = new ProductComparisonStorage()): ProductComparisonApi => {
    return {
        addToList: async (contextId?: unknown, variationId?: unknown): Promise<string | null> => {
            if (typeof contextId !== 'string') {
                throw illegalParameterError('contextId', contextId);
            }

            if (typeof variationId !== 'string') {
                throw illegalParameterError('variationId', variationId);
            }

            const list = storage.retrieveStoredListForContextId(contextId);

            if (list) {
                // Ensure uniqueness of variation ID in list while also ensuring order in which variations were selected for comparison
                storage.storeListForContextId(contextId, [...list.filter(el => el !== variationId), variationId]);
                return !list.includes(variationId) ? variationId : null;
            } else {
                storage.storeListForContextId(contextId, [variationId]);
                return variationId;
            }
        },

        removeFromList: async (contextId?: unknown, variationId?: unknown): Promise<string | null> => {
            if (typeof contextId !== 'string') {
                throw illegalParameterError('contextId', contextId);
            }

            if (typeof variationId !== 'string') {
                throw illegalParameterError('variationId', variationId);
            }

            const list = storage.retrieveStoredListForContextId(contextId);

            if (list === null || !list.includes(variationId)) {
                return null;
            } else if (list.length === 1) {
                // given variation ID was sole one on list -> remove list
                storage.removeStoredListForContextId(contextId);
                return variationId;
            } else {
                storage.storeListForContextId(
                    contextId,
                    list.filter(el => el !== variationId)
                );

                return variationId;
            }
        },

        removeList: async (contextId?: unknown): Promise<string | null> => {
            if (typeof contextId !== 'string') {
                throw illegalParameterError('contextId', contextId);
            }

            return storage.removeStoredListForContextId(contextId);
        },

        getList: async (contextId?: unknown): Promise<string[] | null> => {
            if (typeof contextId !== 'string') {
                throw illegalParameterError('contextId', contextId);
            }

            return storage.retrieveStoredListForContextId(contextId);
        },

        getLists: async (): Promise<string[] | null> => {
            const storedListsByContextId = storage.retrieveStoredListsByContextId();

            return storedListsByContextId === null ? null : Object.keys(storedListsByContextId);
        },
        isOnList: async (contextId?: unknown, variationId?: unknown): Promise<boolean> => {
            if (typeof contextId !== 'string') {
                throw illegalParameterError('contextId', contextId);
            }

            if (typeof variationId !== 'string') {
                throw illegalParameterError('variationId', variationId);
            }

            const list = storage.retrieveStoredListForContextId(contextId);
            return list !== null && list.includes(variationId);
        },

        getLinkForList: async (contextId?: unknown): Promise<string | null> => {
            if (typeof contextId !== 'string') {
                throw illegalParameterError('contextId', contextId);
            }

            const list = storage.retrieveStoredListForContextId(contextId);

            return list === null ? null : `/vergleich/?variationIds=${list.join(',')}`;
        },
    };
};

const illegalParameterError = (name: string, value: unknown): Error => {
    return new Error(`Illegal parameter '${name}'; value: ${JSON.stringify(value)}`);
};

export const productComparisonLoadedTopic = 'ft5.productComparison.loaded';

export const emitProductComparisonLoadedEvent = (bus: QBus): void => {
    bus.emitRetain(productComparisonLoadedTopic, createProductComparison());
};
